import React, { useState, useEffect, useMemo } from 'react';
import { getVersionInfo, getEnvironmentBadgeColor, getEnvironmentBadgeText } from '../services/versionService';
import styles from '../styles/aboutModal.module.css';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ReleaseNote {
  version: string;
  date: string;
  notes: string[];
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function extractSectionHeadings(markdown: string): Array<{ title: string; id: string }> {
  const headings: Array<{ title: string; id: string }> = [];
  for (const line of markdown.split('\n')) {
    const match = line.match(/^## (.+)$/);
    if (match) {
      const title = match[1].trim();
      headings.push({ title, id: slugifyHeading(title) });
    }
  }
  return headings;
}

function renderAboutMarkdown(markdown: string): string {
  const h3 = styles.aboutSubheading;
  const list = styles.aboutList;
  const para = styles.aboutParagraph;
  const code = styles.aboutCode;

  return markdown
    .replace(/^# (.*$)/gim, '')
    .replace(/^## (.*$)/gim, (_match, title: string) => {
      const id = slugifyHeading(title);
      return `<h2 id="${id}" class="${styles.aboutSectionHeading}">${title}</h2>`;
    })
    .replace(/^### (.*$)/gim, `<h3 class="${h3}">$1</h3>`)
    .replace(/^#### (.*$)/gim, `<h4 class="${h3}">$1</h4>`)
    .replace(/^- (.*$)/gim, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (block) => `<ul class="${list}">${block}</ul>`)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, `<code class="${code}">$1</code>`)
    .replace(/\n\n/g, `</p><p class="${para}">`)
    .replace(/^/, `<p class="${para}">`)
    .replace(/$/, '</p>')
    .replace(new RegExp(`<p class="${para}"></p>`, 'g'), '')
    .replace(new RegExp(`<p class="${para}">(<h[234])`, 'g'), '$1')
    .replace(/(<\/h[234]>)<\/p>/g, '$1')
    .replace(new RegExp(`<p class="${para}">(<ul)`, 'g'), '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1');
}

const VERSION_HISTORY_ID = 'version-history';

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  const [versionInfo, setVersionInfo] = useState<any>(null);
  const [badgeColor, setBadgeColor] = useState<string>('');
  const [badgeText, setBadgeText] = useState<string>('');
  const [aboutContent, setAboutContent] = useState<string>('');
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [showEarlierVersions, setShowEarlierVersions] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const loadVersionInfo = async () => {
        try {
          const info = await getVersionInfo();
          setVersionInfo(info);
          setBadgeColor(getEnvironmentBadgeColor());
          setBadgeText(getEnvironmentBadgeText());
        } catch (error) {
          console.error('Error loading version info:', error);
          setVersionInfo({
            version: '1.0.0',
            environment: 'Local Development',
            lastDeployed: new Date().toLocaleString(),
          });
          setBadgeColor(getEnvironmentBadgeColor());
          setBadgeText(getEnvironmentBadgeText());
        }
      };

      loadVersionInfo();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetch('/content/Recovery-Commons-About.md')
        .then((response) => response.text())
        .then((content) => setAboutContent(content))
        .catch((error) => {
          console.error('Error loading about content:', error);
          setAboutContent('# About Recovery Commons\n\nUnable to load content at this time.');
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetch('/content/release-notes.json')
        .then((response) => (response.ok ? response.json() : []))
        .then((data: ReleaseNote[]) => setReleaseNotes(Array.isArray(data) ? data : []))
        .catch(() => setReleaseNotes([]));
    }
  }, [isOpen]);

  const sectionHeadings = useMemo(() => extractSectionHeadings(aboutContent), [aboutContent]);
  const visibleReleaseNotes = showEarlierVersions ? releaseNotes : releaseNotes.slice(0, 2);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>About Recovery Commons</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {(sectionHeadings.length > 0 || releaseNotes.length > 0) && (
            <nav className={styles.tableOfContents} aria-label="About contents">
              <p className={styles.tocTitle}>Contents</p>
              <ul className={styles.tocList}>
                {sectionHeadings.map((heading) => (
                  <li key={heading.id}>
                    <button
                      type="button"
                      className={styles.tocLink}
                      onClick={() => scrollToSection(heading.id)}
                    >
                      {heading.title}
                    </button>
                  </li>
                ))}
                {releaseNotes.length > 0 && (
                  <li>
                    <button
                      type="button"
                      className={styles.tocLink}
                      onClick={() => scrollToSection(VERSION_HISTORY_ID)}
                    >
                      Version History
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          )}

          <div className={styles.aboutContent}>
            {aboutContent ? (
              <div dangerouslySetInnerHTML={{ __html: renderAboutMarkdown(aboutContent) }} />
            ) : (
              <div className={styles.loadingMessage}>Loading about content...</div>
            )}
          </div>

          {releaseNotes.length > 0 && (
            <section id={VERSION_HISTORY_ID} className={styles.versionHistorySection}>
              <h3 className={styles.versionHistoryTitle}>Version History</h3>
              <div className={styles.versionHistoryList}>
                {visibleReleaseNotes.map((note, index) => (
                  <div
                    key={`${note.version}-${note.date}`}
                    className={styles.versionHistoryEntry}
                    style={{
                      borderBottom:
                        index < visibleReleaseNotes.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    <div className={styles.versionHistoryMeta}>
                      <span className={styles.versionHistoryVersion}>v{note.version}</span>
                      <span className={styles.versionHistoryDate}>
                        {new Date(note.date + 'T12:00:00').toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                    <ul className={styles.versionHistoryNotes}>
                      {note.notes.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {releaseNotes.length > 2 && (
                <button
                  type="button"
                  className={styles.showEarlierButton}
                  onClick={() => setShowEarlierVersions((prev) => !prev)}
                >
                  {showEarlierVersions ? 'Show fewer versions' : 'Show earlier versions'}
                </button>
              )}
            </section>
          )}

          <div className={styles.versionFooter}>
            {versionInfo ? (
              <div className={styles.versionFooterRow}>
                <div
                  className={styles.environmentBadge}
                  style={{ backgroundColor: badgeColor }}
                >
                  {badgeText}
                </div>
                <span>
                  <strong>v{versionInfo.version}</strong>
                </span>
                <span>•</span>
                <span>{versionInfo.environment}</span>
                <span>•</span>
                <span>Deployed: {versionInfo.lastDeployed}</span>
              </div>
            ) : (
              <div className={styles.loadingMessage}>Loading version information...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutModal;
